'use client'

import * as React from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import { DayPicker, DropdownProps } from 'react-day-picker'

import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select/select'
import { ScrollArea } from '@/components/ui/scroll-area/scroll-area'

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  formatType?: 'day' | 'month'
}

const Chevron = (props: {
  className?: string
  size?: number
  disabled?: boolean
  orientation?: 'left' | 'right' | 'up' | 'down'
}) => {
  const { className, size, disabled } = props

  if (props.orientation === 'left') {
    return (
      <ChevronLeftIcon
        className={cn('size-4', className)}
        width={size}
        height={size}
        aria-disabled={disabled}
      />
    )
  }
  return (
    <ChevronRightIcon
      className={cn('size-4', className)}
      width={size}
      height={size}
      aria-disabled={disabled}
    />
  )
}

function DropdownComponent({
  value,
  onChange,
  options,
}: Readonly<DropdownProps>) {
  const selected = options?.find((option) => option.value === value)

  const handleChange = (val: string) => {
    const changeEvent = {
      target: { value: val },
    } as React.ChangeEvent<HTMLSelectElement>
    onChange?.(changeEvent)
  }

  return (
    <Select
      value={value?.toString()}
      onValueChange={(val) => handleChange(val)}
    >
      <SelectTrigger
        className="max-w-max pr-1.5 focus:ring-0"
        transactionName="calendar.dropdown.select"
      >
        <SelectValue>{selected?.label}</SelectValue>
      </SelectTrigger>
      <SelectContent position="popper">
        <ScrollArea className="h-60">
          {options?.map((option, id: number) => (
            <SelectItem
              key={`${option.value}-${id}`}
              value={option.value?.toString() ?? ''}
              disabled={option.disabled}
            >
              {option.label}
            </SelectItem>
          ))}
        </ScrollArea>
      </SelectContent>
    </Select>
  )
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  defaultMonth,
  formatType = 'day',
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      hideNavigation={true}
      captionLayout="dropdown"
      startMonth={new Date(1900, 0)}
      endMonth={new Date(2050, 0)}
      className={cn('p-3 px-7', className)}
      defaultMonth={defaultMonth}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        month_caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'text-sm font-medium',
        dropdowns: 'flex justify-center gap-1',
        nav: 'space-x-1 flex items-center',
        button_previous: cn(
          buttonVariants({ variant: 'outline' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
        ),
        button_next: cn(
          buttonVariants({ variant: 'outline' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
        ),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        month_grid: cn(
          'w-full border-collapse space-y-1',
          formatType === 'month' && 'hidden'
        ),
        weekdays: 'flex',
        weekday:
          'text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]',
        week: 'flex w-full mt-2',
        day: cn(
          'text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
          props.mode === 'range'
            ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
            : '[&:has([aria-selected])]:rounded-md'
        ),
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-8 w-8 p-0 font-normal aria-selected:opacity-100'
        ),
        range_start: 'day-range-start',
        range_end: 'day-range-end',
        selected:
          'h-8 w-8 p-0 rounded-md bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        today: 'h-8 w-8 p-0 rounded-md bg-accent text-accent-foreground',
        outside:
          'day-outside text-muted-foreground opacity-50  aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
        disabled: 'text-muted-foreground opacity-50',
        range_middle:
          'aria-selected:bg-accent aria-selected:text-accent-foreground',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Dropdown: DropdownComponent,
        Chevron,
      }}
      {...props}
    />
  )
}

Calendar.displayName = 'Calendar'

export { Calendar }
